/**
 * User model for authentication and authorization
 */
import { v4 as uuidv4 } from 'uuid';

// Define user roles
export const USER_ROLES = {
  ADMIN: 'admin',
  PRACTITIONER: 'practitioner',
  RECEPTIONIST: 'receptionist',
  STAFF: 'staff'
};

// 支持的资源（与侧边栏页面一致）
export const USER_RESOURCES = [
  'customers', 'appointments', 'treatmentRecords', 'treatments', 'practitioners',
  'venues', 'reports', 'settings', 'users', 'datasets', 'researchProjects'
];

class User {
  /**
   * Constructor
   * @param {Object} userData - User data
   */
  constructor(userData = {}) {
    this.id = userData.id || uuidv4();
    this.username = userData.username || '';
    this.password = userData.password || '';  // This should be hashed
    this.name = userData.name || '';
    this.email = userData.email || '';
    this.phone = userData.phone || '';
    this.phoneNumber = userData.phoneNumber || userData.phone || '';
    this.role = userData.role || USER_ROLES.STAFF;
    this.isActive = userData.isActive !== undefined ? userData.isActive : true;
    this.lastLogin = userData.lastLogin || null;
    this.createdAt = userData.createdAt || new Date().toISOString();
    this.updatedAt = userData.updatedAt || new Date().toISOString();
    this.permissions = userData.permissions || this.getDefaultPermissions();
    this.avatar = userData.avatar || null;

    // Practitioner-specific fields
    this.title = userData.title || '';
    this.specialties = userData.specialties || [];
    this.bio = userData.bio || '';
    this.introduction = userData.introduction || userData.bio || '';
  }

  /**
   * Get default permissions based on role
   * @returns {Object} Default permissions
   * @private
   */
  getDefaultPermissions() {
    switch (this.role) {
      case USER_ROLES.ADMIN:
        // 管理员拥有所有权限
        return Object.fromEntries(USER_RESOURCES.map(res => [res, { view: true, create: true, update: true, delete: true }]));
      case USER_ROLES.PRACTITIONER:
        // 调理师权限
        return {
          customers: { view: true, create: false, update: false, delete: false },
          appointments: { view: true, create: true, update: true, delete: false },
          treatmentRecords: { view: true, create: true, update: true, delete: false },
          treatments: { view: true, create: false, update: false, delete: false },
          practitioners: { view: true, create: false, update: false, delete: false },
          venues: { view: true, create: false, update: false, delete: false },
          users: { view: false, create: false, update: false, delete: false },
          reports: { view: true, create: false, update: false, delete: false },
          settings: { view: false, create: false, update: false, delete: false },
          datasets: { view: false, create: false, update: false, delete: false },
          researchProjects: { view: false, create: false, update: false, delete: false }
        };
      case USER_ROLES.RECEPTIONIST:
        // 前台权限
        return {
          customers: { view: true, create: true, update: true, delete: false },
          appointments: { view: true, create: true, update: true, delete: true },
          treatmentRecords: { view: true, create: false, update: false, delete: false },
          treatments: { view: true, create: false, update: false, delete: false },
          practitioners: { view: true, create: false, update: false, delete: false },
          venues: { view: true, create: false, update: false, delete: false },
          users: { view: false, create: false, update: false, delete: false },
          reports: { view: true, create: false, update: false, delete: false },
          settings: { view: false, create: false, update: false, delete: false },
          datasets: { view: false, create: false, update: false, delete: false },
          researchProjects: { view: false, create: false, update: false, delete: false }
        };
      case USER_ROLES.STAFF:
      default:
        // 普通员工权限
        return {
          customers: { view: true, create: true, update: true, delete: false },
          appointments: { view: true, create: true, update: true, delete: false },
          treatmentRecords: { view: true, create: true, update: true, delete: false },
          treatments: { view: true, create: false, update: false, delete: false },
          practitioners: { view: true, create: false, update: false, delete: false },
          venues: { view: true, create: false, update: false, delete: false },
          users: { view: false, create: false, update: false, delete: false },
          reports: { view: true, create: false, update: false, delete: false },
          settings: { view: false, create: false, update: false, delete: false },
          datasets: { view: false, create: false, update: false, delete: false },
          researchProjects: { view: false, create: false, update: false, delete: false }
        };
    }
  }

  /**
   * Update user data
   * @param {Object} userData - User data to update
   * @returns {User} Updated user instance
   */
  update(userData) {
    // Update all fields that are provided
    Object.keys(userData).forEach(key => {
      if (userData[key] !== undefined && key !== 'id') {
        this[key] = userData[key];
      }
    });

    // Always update the updatedAt timestamp
    this.updatedAt = new Date().toISOString();

    return this;
  }

  /**
   * Record login
   * @returns {User} Updated user instance
   */
  recordLogin() {
    this.lastLogin = new Date().toISOString();
    return this;
  }

  /**
   * Set user permissions
   * @param {Object} permissions - Permission object
   * @returns {User} Updated user instance
   */
  setPermissions(permissions) {
    this.permissions = {
      ...this.permissions,
      ...permissions
    };
    this.updatedAt = new Date().toISOString();
    return this;
  }

  /**
   * Check if user has permission
   * @param {string} resource - Resource name
   * @param {string} action - Action name
   * @returns {boolean} Whether user has permission
   */
  hasPermission(resource, action) {
    if (!this.permissions || !this.permissions[resource]) {
      return false;
    }

    const resourcePermissions = this.permissions[resource];

    // Direct permission check
    if (resourcePermissions[action] === true) {
      return true;
    }

    // Handle composite permissions
    if (action === 'write') {
      // Write permission includes create, update, and delete
      return resourcePermissions.create === true ||
        resourcePermissions.update === true ||
        resourcePermissions.delete === true;
    }

    if (action === 'read') {
      // Read permission is an alias for view
      return resourcePermissions.view === true;
    }

    return false;
  }

  /**
   * Activate user
   * @returns {User} Updated user instance
   */
  activate() {
    this.isActive = true;
    this.updatedAt = new Date().toISOString();
    return this;
  }

  /**
   * Deactivate user
   * @returns {User} Updated user instance
   */
  deactivate() {
    this.isActive = false;
    this.updatedAt = new Date().toISOString();
    return this;
  }

  /**
   * Convert to JSON for storage
   * @returns {Object} JSON representation
   */
  toJSON() {
    return {
      id: this.id,
      username: this.username,
      password: this.password,
      name: this.name,
      email: this.email,
      phone: this.phone,
      phoneNumber: this.phoneNumber,
      role: this.role,
      isActive: this.isActive,
      lastLogin: this.lastLogin,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      permissions: this.permissions,
      avatar: this.avatar,
      title: this.title,
      specialties: this.specialties,
      bio: this.bio,
      introduction: this.introduction
    };
  }

  /**
   * Create user from JSON data
   * @param {Object} json - JSON data
   * @returns {User} User instance
   * @static
   */
  static fromJSON(json) {
    return new User(json);
  }

  /**
   * 获取所有用户（本地存储）
   * @returns {User[]} 用户数组
   */
  static 获取所有用户() {
    const users = JSON.parse(localStorage.getItem('users') || '[]');
    return users.map(User.fromJSON);
  }

  /**
   * 新增用户（本地存储）
   * @param {Object} userData 用户数据
   * @returns {User} 新用户
   */
  static 新增用户(userData) {
    const users = User.获取所有用户();
    const newUser = new User(userData);
    users.push(newUser);
    localStorage.setItem('users', JSON.stringify(users.map(u => u.toJSON())));
    return newUser;
  }

  /**
   * 更新用户（本地存储）
   * @param {string} id 用户ID
   * @param {Object} updateData 更新数据
   * @returns {User} 更新后的用户
   */
  static 更新用户(id, updateData) {
    const users = User.获取所有用户();
    const idx = users.findIndex(u => u.id === id);
    if (idx === -1) throw new Error('未找到用户');
    users[idx].update(updateData);
    localStorage.setItem('users', JSON.stringify(users.map(u => u.toJSON())));
    return users[idx];
  }

  /**
   * 删除用户（本地存储）
   * @param {string} id 用户ID
   */
  static 删除用户(id) {
    let users = User.获取所有用户();
    users = users.filter(u => u.id !== id);
    localStorage.setItem('users', JSON.stringify(users.map(u => u.toJSON())));
  }
}

export default User;